import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, ToggleButton, ToggleButtonGroup, Select, MenuItem, Checkbox, IconButton, Tooltip,
  CircularProgress, Alert, Chip,
} from '@mui/material';
import { bloomberg } from '../theme/bloombergTheme';
import { getModelLeague, getLeagueHoldings, LeagueRow, LeagueHolding, LeaguePeriod } from '../services/api';

/**
 * Every model ranked on one screen. Figures come from our stored daily closes
 * (backend league_table.py): returns over 1W–1Y, volatility, Sharpe, breadth
 * (share of names above their 200-day average), trend and today's biggest
 * mover. Rank arrows compare with the same ranking a week ago.
 *
 * Click a row to open the model; ▸ peeks at its holdings in place; ticking two
 * rows offers a side-by-side comparison.
 */

type Scope = 'themes' | 'subs' | 'all';
type RankBy = LeaguePeriod | 'sharpe' | 'breadth' | 'vol';
const PERIODS: LeaguePeriod[] = ['1W', '1M', '3M', 'YTD', '1Y'];
const KIND_LABEL: Record<string, string> = {
  theme: 'Theme', active: 'Active', curated_theme: 'Curated', regional: 'Regional', conviction: 'Conviction',
  allocation: 'Allocation', screen: 'Screen',
};
const MONO = bloomberg.font.mono;

const pct = (v: number | null | undefined, d = 1) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(d)}%`);
const heat = (v: number | null | undefined): React.CSSProperties => {
  if (v === null || v === undefined) return { color: bloomberg.text.muted };
  const a = Math.min(0.55, Math.abs(v * 100) / 60 + 0.06);
  return v >= 0 ? { background: `rgba(34,197,94,${a.toFixed(2)})`, color: '#dcfce7' } : { background: `rgba(239,68,68,${a.toFixed(2)})`, color: '#fee2e2' };
};
const TREND_COLOR: Record<string, string> = {
  up: '#86efac', cross_up: '#86efac', pullback: '#fcd34d', bounce: '#fcd34d', down: '#fca5a5', cross_down: '#fca5a5',
};

const valueOf = (r: LeagueRow, key: RankBy, weekAgo = false): number | null => {
  if (key === 'sharpe') return weekAgo ? null : r.sharpe;
  if (key === 'breadth') return weekAgo ? null : r.breadth;
  if (key === 'vol') return weekAgo ? null : (r.vol === null ? null : -r.vol);     // lower volatility ranks higher
  return (weekAgo ? r.returns_week_ago : r.returns)[key] ?? null;
};
const rankOf = (rows: LeagueRow[], key: RankBy, weekAgo = false) => {
  const sorted = [...rows].sort((a, b) => (valueOf(b, key, weekAgo) ?? -Infinity) - (valueOf(a, key, weekAgo) ?? -Infinity));
  return new Map(sorted.map((r, i) => [r.id, i + 1]));
};

export default function LeagueTable({ onOpen }: {
  onOpen: (id: number, compareWith?: number) => void;
}) {
  const [data, setData] = useState<{ as_of: string | null; models: LeagueRow[]; method: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<Scope>('themes');
  const [rankBy, setRankBy] = useState<RankBy>('3M');
  const [subParent, setSubParent] = useState<string>('');
  const [openParents, setOpenParents] = useState<Set<string>>(new Set());
  const [peek, setPeek] = useState<number | null>(null);
  const [peekRows, setPeekRows] = useState<LeagueHolding[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    getModelLeague().then(setData).catch(() => setError('Could not load the rankings.'));
  }, []);

  useEffect(() => {
    if (peek === null) return;
    setPeekRows(null);
    getLeagueHoldings(peek).then(setPeekRows).catch(() => setPeekRows([]));
  }, [peek]);

  // Visible rows, in order, with depth (0 = ranked row, 1 = sub-theme under its theme).
  const rows = useMemo(() => {
    if (!data) return [] as { r: LeagueRow; depth: number; rank: number | null; move: number | null; kids: number }[];
    const all = data.models;
    const themeRows = all.filter((r) => r.kind === 'theme');
    // Active versions are ranked on their own page (Portfolio › Active), against their bases.
    const pool = scope === 'all' ? all.filter((r) => r.kind !== 'active')
      : scope === 'subs' ? themeRows.filter((r) => !!r.theme_parent && (!subParent || r.theme_parent === subParent))
      : themeRows.filter((r) => !r.theme_parent);
    const now = rankOf(pool, rankBy);
    const before = rankOf(pool, rankBy, true);
    const ranked = [...pool].sort((a, b) => (now.get(a.id) || 0) - (now.get(b.id) || 0));
    const out: { r: LeagueRow; depth: number; rank: number | null; move: number | null; kids: number }[] = [];
    ranked.forEach((r) => {
      const kids = scope === 'themes' ? themeRows.filter((k) => k.theme_parent && k.theme_parent === r.theme) : [];
      const hadPrev = valueOf(r, rankBy, true) !== null;
      out.push({ r, depth: 0, rank: now.get(r.id) || null, move: hadPrev ? (before.get(r.id) || 0) - (now.get(r.id) || 0) : null, kids: kids.length });
      if (kids.length && openParents.has(r.theme || '')) {
        [...kids].sort((a, b) => (valueOf(b, rankBy) ?? -Infinity) - (valueOf(a, rankBy) ?? -Infinity))
          .forEach((k) => out.push({ r: k, depth: 1, rank: null, move: null, kids: 0 }));
      }
    });
    return out;
  }, [data, scope, subParent, rankBy, openParents]);
  const parents = useMemo(() => (data?.models || [])
    .filter((r) => r.kind === 'theme' && !r.theme_parent && r.theme && (data?.models || []).some((k) => k.theme_parent === r.theme))
    .map((r) => [r.theme as string, r.label] as const)
    .sort((a, b) => a[1].localeCompare(b[1])), [data]);

  const toggleParent = (theme: string | null) => {
    if (!theme) return;
    setOpenParents((prev) => { const n = new Set(prev); n.has(theme) ? n.delete(theme) : n.add(theme); return n; });
  };
  const toggleSelect = (id: number) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(-2)));
  const nameOf = (id: number) => {
    const m = data?.models.find((x) => x.id === id);
    return m ? (m.parent_label ? `${m.parent_label} › ${m.label}` : m.label) : '';
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>;

  const th: React.CSSProperties = { textAlign: 'right', padding: '8px 6px', color: bloomberg.text.muted, fontWeight: 600, borderBottom: `1px solid ${bloomberg.border.secondary}`, whiteSpace: 'nowrap', fontSize: 12 };
  const td: React.CSSProperties = { padding: '6px', borderBottom: `1px solid ${bloomberg.border.primary}`, textAlign: 'right', fontFamily: MONO, fontSize: 12.5, whiteSpace: 'nowrap' };
  const colHead = (key: RankBy, label: string) => (
    <th scope="col" style={{ ...th, ...(rankBy === key ? { color: bloomberg.accent.orangeLight, boxShadow: `inset 0 -2px 0 ${bloomberg.accent.orange}` } : {}), cursor: 'pointer' }}
      onClick={() => setRankBy(key)} aria-sort={rankBy === key ? 'descending' : 'none'}>
      {label}
    </th>
  );
  const peekMetric: 'r1m' | 'r3m' | 'r1y' = rankBy === '1M' || rankBy === '1W' ? 'r1m' : rankBy === '1Y' || rankBy === 'YTD' ? 'r1y' : 'r3m';

  return (
    <Box sx={{ position: 'relative', pb: selected.length ? 9 : 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: bloomberg.text.primary }}>Rankings</Typography>
          <Typography sx={{ fontSize: '0.76rem', color: bloomberg.text.muted }}>
            Click a row to open the model · ▸ to peek at its holdings · tick two to compare · click a column to rank by it.
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => v && setScope(v)}>
          <ToggleButton value="themes" sx={{ textTransform: 'none', fontSize: 12 }}>Themes</ToggleButton>
          <ToggleButton value="subs" sx={{ textTransform: 'none', fontSize: 12 }}>Sub-themes</ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: 12 }}>All models</ToggleButton>
        </ToggleButtonGroup>
        {scope === 'subs' && (
          <Select size="small" value={subParent} displayEmpty onChange={(e) => setSubParent(e.target.value as string)} sx={{ fontSize: 12, minWidth: 170 }}
            inputProps={{ 'aria-label': 'Sub-themes of' }}>
            <MenuItem value="">All sub-themes</MenuItem>
            {parents.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </Select>
        )}
        <Select size="small" value={rankBy} onChange={(e) => setRankBy(e.target.value as RankBy)} sx={{ fontSize: 12, minWidth: 150 }}
          inputProps={{ 'aria-label': 'Rank by' }}>
          {PERIODS.map((p) => <MenuItem key={p} value={p}>Rank by {p} return</MenuItem>)}
          <MenuItem value="sharpe">Rank by Sharpe (1Y)</MenuItem>
          <MenuItem value="breadth">Rank by breadth</MenuItem>
          <MenuItem value="vol">Rank by lowest volatility</MenuItem>
        </Select>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', color: bloomberg.text.primary }}>
          <thead>
            <tr>
              <th scope="col" style={{ ...th, width: 30 }}><span style={{ position: 'absolute', left: -9999 }}>Select</span></th>
              <th scope="col" style={{ ...th, textAlign: 'left', width: 58 }}>Rank</th>
              <th scope="col" style={{ ...th, textAlign: 'left' }}>Model</th>
              {PERIODS.map((p) => <React.Fragment key={p}>{colHead(p, p)}</React.Fragment>)}
              {colHead('vol', 'Vol')}
              {colHead('sharpe', 'Sharpe')}
              {colHead('breadth', 'Breadth')}
              <th scope="col" style={{ ...th, textAlign: 'left' }}>Trend</th>
              <th scope="col" style={{ ...th, textAlign: 'left' }}>Biggest mover today</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ r, depth, rank, move, kids }) => {
              const isPeek = peek === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr style={{ background: isPeek ? 'rgba(255,140,0,0.08)' : undefined, cursor: 'pointer' }}
                    onClick={() => onOpen(r.id)}>
                    <td style={{ ...td, textAlign: 'center', padding: '2px' }} onClick={(e) => e.stopPropagation()}>
                      <Checkbox size="small" checked={selected.includes(r.id)} onChange={() => toggleSelect(r.id)}
                        inputProps={{ 'aria-label': `Select ${r.label} to compare` }} sx={{ p: 0.5 }} />
                    </td>
                    <td style={{ ...td, textAlign: 'left' }}>
                      {rank !== null ? String(rank).padStart(2, '0') : ''}
                      {move !== null && move !== 0 && (
                        <span style={{ marginLeft: 4, fontSize: 10.5, color: move > 0 ? '#22c55e' : '#f87171' }}>{move > 0 ? `▲${move}` : `▼${-move}`}</span>
                      )}
                    </td>
                    <th scope="row" style={{ ...td, textAlign: 'left', fontFamily: 'inherit', fontSize: 13, fontWeight: depth ? 500 : 600, paddingLeft: depth ? 28 : 6 }}>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <Tooltip title={isPeek ? 'Hide holdings' : 'Peek at holdings'}>
                          <IconButton size="small" aria-expanded={isPeek} aria-label={`Peek at ${r.label} holdings`}
                            onClick={() => setPeek(isPeek ? null : r.id)} sx={{ p: 0.25, color: isPeek ? bloomberg.accent.orange : bloomberg.text.muted, fontSize: 12, width: 22 }}>
                            {isPeek ? '▾' : '▸'}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {depth ? <>↳ {r.label}</> : r.parent_label ? (
                        <>
                          <span style={{ color: bloomberg.text.muted, fontWeight: 500 }}>{r.parent_label} › </span>{r.label}
                          {scope !== 'subs' && (
                            <Chip size="small" label="sub-theme" variant="outlined"
                              sx={{ ml: 1, height: 16, fontSize: 10, color: bloomberg.text.muted, borderColor: bloomberg.border.secondary }} />
                          )}
                        </>
                      ) : r.label}
                      {scope === 'all' && r.kind && <Chip size="small" label={KIND_LABEL[r.kind] || r.kind} sx={{ ml: 1, height: 16, fontSize: 10 }} />}
                      {kids > 0 && (
                        <Button size="small" onClick={(e) => { e.stopPropagation(); toggleParent(r.theme); }}
                          sx={{ ml: 1, minWidth: 0, py: 0, px: 0.75, fontSize: 11, textTransform: 'none', color: bloomberg.text.muted }}>
                          {openParents.has(r.theme || '') ? '− ' : '+ '}{kids} sub-themes
                        </Button>
                      )}
                      {r.coverage < 0.9 && (
                        <Tooltip title={`${Math.round(r.coverage * 100)}% of names have a full year of stored prices`}>
                          <span style={{ marginLeft: 6, color: '#fcd34d', fontSize: 11 }}>partial history</span>
                        </Tooltip>
                      )}
                    </th>
                    {PERIODS.map((p) => <td key={p} style={{ ...td, ...heat(r.returns[p]) }}>{pct(r.returns[p])}</td>)}
                    <td style={td}>{r.vol === null ? '—' : `${(r.vol * 100).toFixed(0)}%`}</td>
                    <td style={td}>{r.sharpe === null ? '—' : r.sharpe.toFixed(2)}</td>
                    <td style={{ ...td, minWidth: 110 }}>
                      {r.breadth === null ? '—' : (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ flex: 1, height: 7, background: bloomberg.bg.hover, borderRadius: 4, position: 'relative', display: 'block' }}>
                            <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 4, width: `${r.breadth * 100}%`, display: 'block',
                              background: r.breadth >= 0.6 ? '#22c55e' : r.breadth >= 0.4 ? '#f59e0b' : '#ef4444' }} />
                          </span>
                          <span style={{ width: 34 }}>{Math.round(r.breadth * 100)}%</span>
                        </span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'left', fontFamily: 'inherit', fontSize: 12, color: TREND_COLOR[r.trend_code || ''] || bloomberg.text.muted }}>{r.trend || '—'}</td>
                    <td style={{ ...td, textAlign: 'left' }}>
                      {r.mover.symbol ? <>{r.mover.symbol} <span style={{ color: (r.mover.r1d || 0) >= 0 ? '#22c55e' : '#f87171' }}>{pct(r.mover.r1d)}</span></> : '—'}
                    </td>
                  </tr>
                  {isPeek && (
                    <tr>
                      <td colSpan={14} style={{ padding: 0, borderBottom: `1px solid ${bloomberg.border.secondary}` }}>
                        <Box sx={{ bgcolor: bloomberg.bg.tertiary, borderLeft: `2px solid ${bloomberg.accent.orange}`, p: 1.5, pl: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                              {r.n_holdings} holdings{r.equal_weight ? ` · ${(100 / Math.max(1, r.n_holdings)).toFixed(1)}% each` : ''}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>
                              {peekMetric === 'r1m' ? '1M' : peekMetric === 'r1y' ? '1Y' : '3M'} return · amber outline = also in another model (hover for which)
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Button size="small" variant="contained" onClick={() => onOpen(r.id)} sx={{ textTransform: 'none' }}>
                              Open model &amp; analysis →
                            </Button>
                          </Box>
                          {peekRows === null ? <CircularProgress size={16} /> : (
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 0.75 }}>
                              {[...peekRows].sort((a, b) => ((b[peekMetric] ?? -9) - (a[peekMetric] ?? -9))).map((h) => (
                                <Tooltip key={h.symbol} title={h.also_in.length ? `Also in: ${h.also_in.join(', ')}` : ''}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.6, borderRadius: 1, bgcolor: bloomberg.bg.secondary,
                                    border: `1px solid ${h.also_in.length ? 'rgba(245,158,11,0.55)' : bloomberg.border.primary}` }}>
                                    <Typography sx={{ fontFamily: MONO, fontWeight: 600, fontSize: 12.5, width: 74, flexShrink: 0 }} noWrap>{h.symbol}</Typography>
                                    <Typography sx={{ fontSize: 11.5, color: bloomberg.text.muted, flex: 1 }} noWrap>{h.name}</Typography>
                                    <Typography sx={{ fontFamily: MONO, fontSize: 12, color: (h[peekMetric] ?? 0) >= 0 ? '#22c55e' : '#f87171' }}>{pct(h[peekMetric])}</Typography>
                                  </Box>
                                </Tooltip>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </Box>

      <Typography sx={{ fontSize: 11, color: bloomberg.text.muted, mt: 1 }}>
        Prices to {data.as_of || '—'}. {data.method} Rank arrows: change against the same ranking a week ago.
        Breadth: share of a model's names above their 200-day average. Sharpe uses the US 3-month rate.
      </Typography>

      {selected.length > 0 && (
        <Box role="region" aria-label="Compare selection" sx={{
          position: 'sticky', bottom: 12, mx: 'auto', mt: 2, width: 'fit-content', display: 'flex', alignItems: 'center', gap: 1.5,
          bgcolor: bloomberg.bg.hover, border: `1px solid ${bloomberg.border.secondary}`, borderRadius: 2, px: 2, py: 1, boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
        }}>
          <Typography sx={{ fontSize: 13 }}><b>{selected.length} selected:</b> {selected.map(nameOf).join(', ')}</Typography>
          <Button size="small" variant="contained" disabled={selected.length !== 2} onClick={() => onOpen(selected[0], selected[1])} sx={{ textTransform: 'none' }}>
            Compare side by side
          </Button>
          <Button size="small" onClick={() => setSelected([])} sx={{ textTransform: 'none' }}>Clear</Button>
        </Box>
      )}
    </Box>
  );
}
